import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LaunchIcon from "@mui/icons-material/Launch";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";

const BannerContainer = styled(Box)(({ theme }) => ({
  border: `1px solid rgba(80, 72, 229, 0.2)`,
  borderRadius: theme.spacing(1),
  backgroundColor: "rgba(80, 72, 229, 0.03)",
  overflow: "hidden",
  marginTop: theme.spacing(1),
  minHeight: "56px",
}));

const BannerContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.5, 1.5),
  minHeight: "56px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1),
  },
}));

const RankingInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flex: 1,
  minWidth: 0,
}));

const ActionButton = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const ExpandableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 1.5, 1.5, 1.5),
  borderTop: `1px solid rgba(80, 72, 229, 0.1)`,
  backgroundColor: "white",
}));

interface KeywordRankingData {
  readonly keyword: string;
  readonly url: string;
  readonly position: number;
  readonly clicks: number;
  readonly impressions: number;
  readonly ctr: number;
}

interface KeywordRankingChipProps {
  readonly rankingData: KeywordRankingData;
  readonly onOptimizeContent?: () => void;
}

const truncateUrl = (url: string, maxLength: number = 35): string => {
  if (!url || url.length <= maxLength) return url;
  const cleanUrl = url.replace(/^https?:\/\//, "");
  if (cleanUrl.length <= maxLength) return cleanUrl;

  const parts = cleanUrl.split("/");
  const domain = parts[0];

  if (domain.length >= maxLength - 3) {
    return domain.substring(0, maxLength - 3) + "...";
  }

  return domain + "/...";
};

const KeywordRankingChip: React.FC<KeywordRankingChipProps> = ({
  rankingData,
  onOptimizeContent,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const theme = useTheme();

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const handleOptimizeClick = async () => {
    if (onOptimizeContent && !isTransitioning) {
      setIsTransitioning(true);
      // Brief delay to show loading state before transition
      setTimeout(() => {
        onOptimizeContent();
      }, 300);
    }
  };

  return (
    <BannerContainer>
      <BannerContent>
        <RankingInfo>
          <TrendingUpIcon sx={{ fontSize: 20, color: "#5048E5", mr: 1.5 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ color: "#5048E5", fontWeight: 600, lineHeight: 1.2 }}
            >
              Already ranking #{rankingData.position} for "{rankingData.keyword}
              "
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#6b7280", fontSize: "0.75rem" }}
            >
              Update existing content to avoid keyword cannibalization
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{
              color: "#5048E5",
              "&:hover": { backgroundColor: "rgba(80, 72, 229, 0.1)" },
              mr: "5px",
            }}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </RankingInfo>

        <ActionButton>
          {onOptimizeContent && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleOptimizeClick}
              disabled={isTransitioning}
              sx={{
                fontSize: "0.875rem",
                minWidth: "140px",
                height: "36px",

                fontWeight: 600,
                [theme.breakpoints.down("sm")]: {
                  minWidth: "auto",
                  width: "100%",
                },
              }}
            >
              {isTransitioning ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Switching...
                </>
              ) : (
                "Update Article"
              )}
            </Button>
          )}
        </ActionButton>
      </BannerContent>

      <Collapse in={expanded}>
        <ExpandableContent>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1.5, color: "#5048E5" }}
          >
            Already ranking for "{rankingData.keyword}"
          </Typography>

          <Box display="flex" justifyContent="space-between" mb={1.5}>
            <Box textAlign="center">
              <Typography variant="h6" color="primary" fontWeight="bold">
                #{rankingData.position}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Position
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="bold">
                {rankingData.clicks}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Clicks
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="bold">
                {rankingData.impressions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Impressions
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {rankingData.ctr}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                CTR
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1.5 }}
          >
            📅 Performance over last 30 days
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5, fontWeight: 500 }}
            >
              Currently ranking page:
            </Typography>
            <Link
              href={rankingData.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                fontSize: "0.875rem",
                color: "#5048E5",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <Typography
                variant="body2"
                sx={{ flex: 1, fontSize: "0.875rem" }}
              >
                {truncateUrl(rankingData.url, 50)}
              </Typography>
              <LaunchIcon sx={{ fontSize: 16 }} />
            </Link>
          </Box>
        </ExpandableContent>
      </Collapse>
    </BannerContainer>
  );
};

export default KeywordRankingChip;
